import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Video, Camera, Palette, Monitor, Share2, Search, Mic, Compass, Sparkles, Megaphone } from 'lucide-react';
import { STATS } from '../mock';
import { SERVICE_PAGES, serviceSchema } from '../servicePages';
import CTASection from '../components/CTASection';
import ClientMarquee from '../components/ClientMarquee';
import PortfolioGrid from '../components/PortfolioGrid';
import useSEO from '../hooks/useSEO';
import { ServiceHero, ServiceIncluded, ServiceGallery, ServiceApproach, ServiceWhy, ServiceCaseStudies, ServiceFAQ, ServiceRelated } from '../components/ServiceSections';

const ICONS = {
  'marketing-strategy': Compass, 'video-production': Video, photography: Camera, 'graphic-design': Palette,
  'website-design': Monitor, 'social-media': Share2, seo: Search, 'podcast-production': Mic,
  'animation-motion-graphics': Sparkles, 'digital-advertising': Megaphone
};

const ServicePage = () => {
  const { slug } = useParams();
  const s = SERVICE_PAGES[slug];
  useSEO({ title: s?.metaTitle || 'Yellow, Marketing Agency Liverpool', description: s?.metaDescription, path: `/${slug || ''}`, image: s?.hero });

  useEffect(() => {
    if (!s) return undefined;
    document.getElementById('service-jsonld')?.remove();
    const script = document.createElement('script');
    script.id = 'service-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(serviceSchema(slug));
    document.head.appendChild(script);
    return () => script.remove();
  }, [s, slug]);

  if (!s) return <Navigate to="/" />;

  return (
    <>
      <ServiceHero s={s} Icon={ICONS[slug] || Video} />
      <ClientMarquee />
      <ServiceIncluded s={s} />
      {s.videos.length > 0 && (
        <section className="py-16 lg:py-20" data-testid="service-videos">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="mb-12 max-w-3xl">
              <p className="section-label mb-4">Watch</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">{s.videosTitle || "Films we've made."}</h2>
            </div>
            <PortfolioGrid items={s.videos} cols={s.videoCols || 3} />
          </div>
        </section>
      )}
      {s.gallery.length > 0 && <ServiceGallery s={s} />}
      <ServiceApproach s={s} />
      <ServiceWhy s={s} stats={STATS} />
      <ServiceCaseStudies s={s} />
      <ServiceFAQ s={s} />
      <ServiceRelated s={s} />
      <CTASection />
    </>
  );
};

export default ServicePage;
